import { Delete, Download, Edit, Upload } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useCallback, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import forecastService from "../../services/forecast";
import regionService from "../../services/region";
import config from "../../utils/config";
import queryDefault from "../../utils/queryDefault";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

const formatMonth = (month: number) => monthNames[Math.max(0, month - 1)] || String(month);

export default function ForecastList() {
  const [pageSize, setPageSize] = useState<number>(10);
  const [showList, setShowList] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const alert = useAlert();
  const notification = useNotification();

  const form = useForm({
    defaultValues: {
      regions: [] as any[],
      months: 3,
      safety_threshold: 10,
      current_roll_count: 0,
      current_lembar_count: 0,
    },
  });

  const [options, setOptions] = useState<any>({
    regions: "",
    months: 3,
    safety_threshold: 10,
    current_roll_count: 0,
    current_lembar_count: 0,
  });

  const listRegion = useQuery(["forecast-region-options"], () => regionService.getAllRegions({}), {
    ...queryDefault,
  });

  const listForecast = useQuery(
    ["forecast-list", options],
    ({ queryKey }) => forecastService.getAllForecasts(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const deleteForecast = useMutation(forecastService.deleteForecast, {
    onSuccess: () => {
      listForecast.refetch();
      notification.onOpen({
        message: "Forecast was successfully deleted!",
        type: "success",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const uploadForecast = useMutation(forecastService.uploadForecast, {
    onSuccess: () => {
      listForecast.refetch();
      notification.onOpen({
        message: "Forecast uploaded successfully!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to upload forecast",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const createPurchaseFromForecast = useMutation(forecastService.createPurchaseFromForecast, {
    onSuccess: () => {
      notification.onOpen({
        message: "Purchase created successfully from forecast!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to create purchase from forecast",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onDelete = (id: number) => {
    alert.onClose();
    deleteForecast.mutate(id);
  };

  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure to delete this data?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  const onHandleApply = useCallback((values: any) => {
    setOptions({
      regions: (values.regions || []).map((item: any) => item.id).join(","),
      months: Number(values.months) || 0,
      safety_threshold: Number(values.safety_threshold) || 0,
      current_roll_count: Number(values.current_roll_count) || 0,
      current_lembar_count: Number(values.current_lembar_count) || 0,
    });
  }, []);

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadForecast.mutate(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const summary = listForecast.data?.data?.summary || {};
  const forecasts = listForecast.data?.data?.forecasts || [];

  const columns: any = [
    { field: "region_name", headerName: "Region Name", minWidth: 200, flex: 1 },
    {
      field: "month",
      headerName: "Month",
      minWidth: 140,
      valueGetter: ({ value }: any) => formatMonth(value),
    },
    { field: "year", headerName: "Year", width: 100 },
    {
      field: "beginning_stock",
      headerName: "Beginning Stock",
      minWidth: 170,
      valueGetter: ({ row }: any) => (row.roll_count_beginning * 700) + row.kertas_count_beginning,
    },
    {
      field: "end_stock",
      headerName: "End Stock",
      minWidth: 140,
      valueGetter: ({ row }: any) => (row.roll_count_end * 700) + row.kertas_count_end,
    },
    { field: "used_kertas", headerName: "Used Kertas", minWidth: 130 },
    {
      field: "action",
      headerName: "Actions",
      width: 220,
      sortable: false,
      filterable: false,
      renderCell: ({ row }: any) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Edit />}
            component={RouterLink}
            to={`/app/forecast/edit/${row.id}`}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => onClickDelete(row.id)}
          >
            Delete
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <MainLayout>
      <Helmet>
        <title>Forecast</title>
      </Helmet>

      <Breadcrumb
        label="Forecast"
        breadcrumbs={[{ label: "Dashboard", href: "/app" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item lg={6} xs={12}>
              <Controller
                name="regions"
                control={form.control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={listRegion.data?.data || []}
                    getOptionLabel={(option: any) => option?.name || ""}
                    value={field.value}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => <TextField {...params} label="Regions" size="small" />}
                  />
                )}
              />
            </Grid>

            <Grid item lg={2} xs={6}>
              <Controller
                name="months"
                control={form.control}
                render={({ field }) => (
                  <TextField {...field} label="Months" type="number" size="small" fullWidth />
                )}
              />
            </Grid>

            <Grid item lg={2} xs={6}>
              <Controller
                name="safety_threshold"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Safety treshold"
                    type="number"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item lg={2} xs={6}>
              <Controller
                name="current_roll_count"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Current roll count"
                    type="number"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item lg={2} xs={6}>
              <Controller
                name="current_lembar_count"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Current lembar count"
                    type="number"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={form.handleSubmit(onHandleApply)}>
              Apply
            </Button>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                component={RouterLink}
                to="/app/forecast/create"
              >
                Create Data
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => forecastService.downloadForecast(options)}
              >
                Download report
              </Button>
              <LoadingButton
                variant="contained"
                startIcon={<Upload />}
                component="label"
                loading={uploadForecast.isLoading}
              >
                Upload Bulk
                <input ref={fileInputRef} onChange={handleUpload} type="file" hidden />
              </LoadingButton>
              {summary.purchase_qty > 0 && (
                <LoadingButton
                  variant="contained"
                  onClick={() => createPurchaseFromForecast.mutate(options)}
                  loading={createPurchaseFromForecast.isLoading}
                >
                  Create Purchase
                </LoadingButton>
              )}
            </Stack>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Grid container spacing={2}>
              {[
                { label: "Beginning stock", value: summary.beginning_stock ?? 0 },
                { label: "End stock", value: summary.end_stock ?? 0 },
                { label: "Average used", value: summary.average_used ?? 0 },
                { label: "Average used stock", value: summary.average_used_stock ?? 0 },
                { label: "Safety stock", value: summary.safety_stock ?? 0 },
                { label: "Purchase qty", value: summary.purchase_qty ?? 0 },
              ].map((item) => (
                <Grid item lg={4} sm={6} xs={12} key={item.label}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {item.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h6">Forecast List</Typography>
              <Button variant="text" onClick={() => setShowList((prev) => !prev)}>
                {showList ? "Hide" : "Show"}
              </Button>
            </Stack>

            {showList && (
              <Box sx={{ mt: 2, width: "100%", overflowX: "auto" }}>
                <DataGrid
                  autoHeight
                  rows={forecasts}
                  columns={columns}
                  pageSize={pageSize}
                  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  rowsPerPageOptions={config.DEFAULT_PAGE_SIZE}
                  disableSelectionOnClick
                  components={{
                    NoRowsOverlay: CustomNoRowOverlay,
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}